// /src/engine/collision.ts

import * as THREE from 'three';
import { getLocalCoreBoxForObject, getWorldCoreBoxForObject } from './coreBounds';

type OBB = {
  c: THREE.Vector3;
  u: [THREE.Vector3, THREE.Vector3, THREE.Vector3]; // orthonormal basis
  e: THREE.Vector3; // half-extents
};

const _v0 = new THREE.Vector3();
const _v1 = new THREE.Vector3();
const _v2 = new THREE.Vector3();
const _x = new THREE.Vector3();
const _y = new THREE.Vector3();
const _z = new THREE.Vector3();

function obbFromObject(obj: THREE.Object3D): OBB | null {
  const boxLocal = getLocalCoreBoxForObject(obj);
  if (!boxLocal) return null;

  obj.updateMatrix();
  obj.updateMatrixWorld(true);
  obj.matrixWorld.extractBasis(_x, _y, _z);
  _x.normalize();
  _y.normalize();
  _z.normalize();

  const centerLocal = boxLocal.getCenter(_v0);
  const sizeLocal = boxLocal.getSize(_v1);
  const half = sizeLocal.multiplyScalar(0.5);

  const centerWorld = centerLocal.applyMatrix4(obj.matrixWorld);

  return {
    c: centerWorld.clone(),
    u: [_x.clone(), _y.clone(), _z.clone()],
    e: half.clone(),
  };
}

// SAT OBB/OBB intersection (Real-Time Collision Detection)
function obbIntersects(a: OBB, b: OBB): boolean {
  const EPS = 1e-5;

  // Rotation matrix R and absR
  const R: number[][] = [
    [0, 0, 0],
    [0, 0, 0],
    [0, 0, 0],
  ];
  const AbsR: number[][] = [
    [0, 0, 0],
    [0, 0, 0],
    [0, 0, 0],
  ];

  for (let i = 0; i < 3; i++) {
    for (let j = 0; j < 3; j++) {
      R[i][j] = a.u[i].dot(b.u[j]);
      AbsR[i][j] = Math.abs(R[i][j]) + EPS;
    }
  }

  // Translation vector t in a's frame
  _v2.subVectors(b.c, a.c);
  const t = [
    _v2.dot(a.u[0]),
    _v2.dot(a.u[1]),
    _v2.dot(a.u[2]),
  ];

  const ae = [a.e.x, a.e.y, a.e.z];
  const be = [b.e.x, b.e.y, b.e.z];

  let ra = 0;
  let rb = 0;

  // Test axes L = A0, A1, A2
  for (let i = 0; i < 3; i++) {
    ra = ae[i];
    rb = be[0] * AbsR[i][0] + be[1] * AbsR[i][1] + be[2] * AbsR[i][2];
    if (Math.abs(t[i]) > ra + rb) return false;
  }

  // Test axes L = B0, B1, B2
  for (let j = 0; j < 3; j++) {
    ra = ae[0] * AbsR[0][j] + ae[1] * AbsR[1][j] + ae[2] * AbsR[2][j];
    rb = be[j];
    const tj = Math.abs(t[0] * R[0][j] + t[1] * R[1][j] + t[2] * R[2][j]);
    if (tj > ra + rb) return false;
  }

  // Test axis L = A0 x B0
  ra = ae[1] * AbsR[2][0] + ae[2] * AbsR[1][0];
  rb = be[1] * AbsR[0][2] + be[2] * AbsR[0][1];
  if (Math.abs(t[2] * R[1][0] - t[1] * R[2][0]) > ra + rb) return false;

  // L = A0 x B1
  ra = ae[1] * AbsR[2][1] + ae[2] * AbsR[1][1];
  rb = be[0] * AbsR[0][2] + be[2] * AbsR[0][0];
  if (Math.abs(t[2] * R[1][1] - t[1] * R[2][1]) > ra + rb) return false;

  // L = A0 x B2
  ra = ae[1] * AbsR[2][2] + ae[2] * AbsR[1][2];
  rb = be[0] * AbsR[0][1] + be[1] * AbsR[0][0];
  if (Math.abs(t[2] * R[1][2] - t[1] * R[2][2]) > ra + rb) return false;

  // L = A1 x B0
  ra = ae[0] * AbsR[2][0] + ae[2] * AbsR[0][0];
  rb = be[1] * AbsR[1][2] + be[2] * AbsR[1][1];
  if (Math.abs(t[0] * R[2][0] - t[2] * R[0][0]) > ra + rb) return false;

  // L = A1 x B1
  ra = ae[0] * AbsR[2][1] + ae[2] * AbsR[0][1];
  rb = be[0] * AbsR[1][2] + be[2] * AbsR[1][0];
  if (Math.abs(t[0] * R[2][1] - t[2] * R[0][1]) > ra + rb) return false;

  // L = A1 x B2
  ra = ae[0] * AbsR[2][2] + ae[2] * AbsR[0][2];
  rb = be[0] * AbsR[1][1] + be[1] * AbsR[1][0];
  if (Math.abs(t[0] * R[2][2] - t[2] * R[0][2]) > ra + rb) return false;

  // L = A2 x B0
  ra = ae[0] * AbsR[1][0] + ae[1] * AbsR[0][0];
  rb = be[1] * AbsR[2][2] + be[2] * AbsR[2][1];
  if (Math.abs(t[1] * R[0][0] - t[0] * R[1][0]) > ra + rb) return false;

  // L = A2 x B1
  ra = ae[0] * AbsR[1][1] + ae[1] * AbsR[0][1];
  rb = be[0] * AbsR[2][2] + be[2] * AbsR[2][0];
  if (Math.abs(t[1] * R[0][1] - t[0] * R[1][1]) > ra + rb) return false;

  // L = A2 x B2
  ra = ae[0] * AbsR[1][2] + ae[1] * AbsR[0][2];
  rb = be[0] * AbsR[2][1] + be[1] * AbsR[2][0];
  if (Math.abs(t[1] * R[0][2] - t[0] * R[1][2]) > ra + rb) return false;

  return true;
}

export function evaluateCollision(selected: THREE.Object3D, others: THREE.Object3D[]): 'ok' | 'warn' | 'bad' {
  const aOBB = obbFromObject(selected);
  let aAABB: THREE.Box3 | null = null;
  const bAABB = new THREE.Box3();

  let overlaps = 0;

  for (const obj of others) {
    if (!obj.visible) continue;

    let hit = false;
    const bOBB = obbFromObject(obj);

    if (aOBB && bOBB) {
      hit = obbIntersects(aOBB, bOBB);
    } else {
      // Fallback: use core AABBs; if unavailable, fall back to geometry AABB.
      if (!aAABB) {
        if (!getWorldCoreBoxForObject(selected, bAABB)) bAABB.setFromObject(selected);
        aAABB = bAABB.clone();
      }
      if (!getWorldCoreBoxForObject(obj, bAABB)) bAABB.setFromObject(obj);
      hit = aAABB.intersectsBox(bAABB);
    }

    if (hit) overlaps++;
    if (overlaps > 2) break;
  }

  return overlaps === 0 ? 'ok' : overlaps > 2 ? 'bad' : 'warn';
}
